import {
  getFeeScheduleTeamMembers,
  getMarketplaceTeamId,
  withFeeScheduleStaffingSeated,
} from "_shared/fee-schedules/categories/fee-schedule-team-members";
import FeeScheduleTeamRoster from "_shared/fee-schedules/categories/fee-schedule-team-roster";
import { staffingForRates } from "_shared/fee-schedules/categories/fee-schedule-template-staffing";
import { type ReconcileFeeScheduleStaffing } from "_shared/fee-schedules/categories/use-reconcile-fee-schedule-staffing";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import Guid from "_shared/guids/guid";
import { withoutRatesNamedOn } from "_shared/fee-schedules/categories/fee-schedule-template-category";
import FeeSchedule from "marketplace/teams/fee-schedules/fee-schedule";
import { Masked, MaskedTeam } from "work/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

/**
 * The marketplace team whose published rates this draft is offered, if any.
 *
 * Only the vendor's: on the client's draft the vendor's list price would become the client's opening
 * offer, attributed to the client (#4920). A team that did not come from the marketplace has no
 * published rates to offer.
 */
export function publishedFeeScheduleTeamId(params: {
  viewingAsVendor: boolean | undefined;
  vendorTeam: VendorTeam | MaskedTeam | null | undefined;
}): Guid | undefined {
  return params.viewingAsVendor ? getMarketplaceTeamId(params.vendorTeam) : undefined;
}

/** What became of applying a published fee schedule to a draft. */
export type PublishedFeeScheduleApplication =
  | { applied: false }
  | {
      applied: true;
      proposalBuilder: ProposalBuilder;
      /** The schedule's rates that went onto the draft. */
      addedCategoryCount: number;
      /** The schedule's rates left out because the draft already has a rate of that name. */
      skippedCategoryCount: number;
    };

/**
 * Puts a vendor team's published marketplace fee schedule on that team's draft (#4920).
 *
 * The rates go through `ProposalBuilder.setFeeSchedule`, as a fee schedule template's do, so they
 * land as an ordinary fee schedule edit and the History view attributes them to the vendor.
 *
 * Each rate keeps the person who bills it. The published schedule's staffing is checked against the
 * marketplace team when it is written, so on that team's own draft the people normally match and
 * nothing is asked. They can still differ: a member can leave the team after the schedule was
 * published, the draft's team can have been changed, and somebody from the client's legal entity
 * bills nothing even when they sit on the vendor team (#6091). Each of those people is put to the
 * user by `reconcileStaffing`, the same question a template asks, and is never dropped or added
 * without the user's answer.
 *
 * A rate whose name the draft already has is left out, and the draft's own rate stays. Name is a
 * rate's identity on a proposal; the template panel applies the same rule.
 *
 * Nothing is applied when the user dismisses the staffing question.
 *
 * The staffing question waits on the user, and a collaborator can save while it is open. So the
 * rates are applied to `getLatestProposalBuilder()` once it is answered, never to the builder the
 * press started from: the caller queues the whole proposal the builder produces, and a builder from
 * before the dialog would carry the collaborator's changes back out as reversions. The names are
 * checked again against that latest draft, and the people are seated on its team as it now stands.
 */
export async function applyPublishedFeeSchedule(params: {
  proposalBuilder: ProposalBuilder;
  schedule: FeeSchedule;
  reconcileStaffing: ReconcileFeeScheduleStaffing;
  /** The draft as it stands now. Read once the staffing question is answered. */
  getLatestProposalBuilder?: () => ProposalBuilder | undefined;
}): Promise<PublishedFeeScheduleApplication> {
  const { proposalBuilder, schedule, reconcileStaffing, getLatestProposalBuilder } = params;
  const workAgreement = proposalBuilder.currentSpec.workAgreement;

  const publishedCategories = schedule.copyCategoriesForProposal();
  const newCategories = withoutRatesNamedOn(currentFeeScheduleOf(proposalBuilder), publishedCategories);
  if (newCategories.length === 0) {
    return { applied: true, proposalBuilder, addedCategoryCount: 0, skippedCategoryCount: publishedCategories.length };
  }

  const staffing = await reconcileStaffing({
    categories: newCategories,
    teamMembers: getFeeScheduleTeamMembers(currentVendorTeamOf(proposalBuilder)),
    teamIsMasked: Masked.isMasked(workAgreement.vendorTeam),
    excludedLegalEntityId: FeeScheduleTeamRoster.fromVendorTeam(workAgreement.vendorTeam, workAgreement.clientTeam)
      .excludedLegalEntityId,
  });
  if (!staffing.applied) return { applied: false };

  const latestProposalBuilder = getLatestProposalBuilder?.() ?? proposalBuilder;
  const latestCategories = currentFeeScheduleOf(latestProposalBuilder);
  // A collaborator may have added a rate of the same name while the question was open. Theirs stays.
  const categoriesToAdd = withoutRatesNamedOn(latestCategories, staffing.categories);

  // Only the people billing a rate that is added: a skipped rate seats nobody.
  const membersToSeat = staffingForRates(staffing.membersToAddToTeam, categoriesToAdd);
  let updatedProposalBuilder = latestProposalBuilder;
  if (membersToSeat.length > 0) {
    updatedProposalBuilder = updatedProposalBuilder.setVendorTeam(
      withFeeScheduleStaffingSeated(
        currentVendorTeamOf(latestProposalBuilder) ?? new VendorTeam({ members: [] }),
        membersToSeat,
      ),
    );
  }
  if (categoriesToAdd.length > 0) {
    updatedProposalBuilder = updatedProposalBuilder.setFeeSchedule([...latestCategories, ...categoriesToAdd]);
  }

  return {
    applied: true,
    proposalBuilder: updatedProposalBuilder,
    addedCategoryCount: categoriesToAdd.length,
    skippedCategoryCount: publishedCategories.length - categoriesToAdd.length,
  };
}

/** Says which published rates stayed off the draft, because it already had a rate of that name. */
export function publishedRatesSkippedMessage(skippedCount: number, addedCount: number): string {
  const skipped = skippedCount === 1 ? "1 published rate was" : `${skippedCount} published rates were`;
  const outcome = addedCount === 0 ? "Nothing was added: " : "";
  return `${outcome}${skipped} already on this fee schedule by name, and the fee schedule's own rate was kept.`;
}

/**
 * Applies the vendor's chosen published schedule to the draft and tells the vendor what happened.
 *
 * The Fee Schedule tab's **Apply Published Rates** and the offer on a vendor's draft with no rates
 * (#6114, #6468) both run this, so the two give the same staffing question, the same edit and the
 * same messages. It is a module function, not a hook, so neither component's memoization depends on
 * its `try`.
 *
 * @returns true when rates went onto the draft.
 */
export async function applyPublishedFeeScheduleToDraft(params: {
  proposalBuilder: ProposalBuilder;
  schedule: FeeSchedule;
  reconcileStaffing: ReconcileFeeScheduleStaffing;
  /** The draft as it stands now. See `applyPublishedFeeSchedule`. */
  getLatestProposalBuilder?: () => ProposalBuilder | undefined;
  onProposalBuilderUpdated: (proposalBuilder: ProposalBuilder) => void;
}): Promise<boolean> {
  const { onProposalBuilderUpdated, ...application } = params;
  let result: PublishedFeeScheduleApplication;
  try {
    result = await applyPublishedFeeSchedule(application);
  } catch (error) {
    console.error(error);
    showErrorSnackbar(error, { doing: "applying your published rates", operation: "proposal.fee-schedule.apply-published-rates" });
    return false;
  }
  if (!result.applied) return false;
  if (result.addedCategoryCount > 0) onProposalBuilderUpdated(result.proposalBuilder);
  if (result.skippedCategoryCount > 0) {
    enqueueSnackbar(publishedRatesSkippedMessage(result.skippedCategoryCount, result.addedCategoryCount), {
      variant: "info",
    });
  }
  return result.addedCategoryCount > 0;
}

function currentFeeScheduleOf(proposalBuilder: ProposalBuilder) {
  const feeSchedule = proposalBuilder.currentSpec.workAgreement.feeSchedule;
  return Masked.isPresentNotMasked(feeSchedule) ? feeSchedule : [];
}

function currentVendorTeamOf(proposalBuilder: ProposalBuilder) {
  const vendorTeam = proposalBuilder.currentSpec.workAgreement.vendorTeam;
  return Masked.isPresentNotMasked(vendorTeam) ? vendorTeam : undefined;
}
